import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { httpClient } from "../../services/httpClient";
import * as R from "remeda";
import Fuse from "fuse.js";
import { hoursToMilliseconds } from "date-fns";

export const useGetAllProgramsAndTreatmentsQuery = (languageCode?: string) =>
  useQuery<ProgramsAndTreatments>({
    queryKey: ["programsAndTreatments", { languageCode }],
    queryFn: async () => {
      const { data } = await httpClient.get<ProgramsAndTreatmentsFromApi>(
        "/indianapolis/programs-and-treatments",
        {
          params: {
            languageCode,
          },
        },
      );

      const treatmentsWithPrograms = R.mapValues(
        data.treatments,
        (treatment) => ({
          ...R.omit(treatment, ["programIds"]),
          programs: treatment.programIds.map((id) => data.programs[id]),
        }),
      );
      const treatmentObjects = Object.values(treatmentsWithPrograms);
      const fuse = new Fuse<TreatmentDenormalized>(treatmentObjects, {
        includeScore: true,
        isCaseSensitive: false,
        threshold: 0.33,
        keys: [
          { name: "title", weight: 0.8 },
          { name: "programs.title", weight: 0.2 },
        ],
      });

      return {
        treatments: treatmentsWithPrograms,
        programs: R.mapValues(data.programs, (program) => ({
          ...R.omit(program, ["treatmentId"]),
          treatment: data.treatments[program.treatmentId],
        })),
        searchTree: fuse,
      };
    },
    staleTime: hoursToMilliseconds(12),
  });

export type TreatmentFromApi = {
  id: string;
  title: string;
  description: string;
  introduction: boolean;
  free: boolean;
  thumbnailUrl: string;
  standaloneProgramsContainer: boolean;
  programIds: string[];
};

export type ProgramFromApi = {
  id: string;
  title: string;
  standalone: boolean;
  treatmentId: string;
};

type ProgramsAndTreatmentsFromApi = {
  treatments: Record<string, TreatmentFromApi>;
  programs: Record<string, ProgramFromApi>;
};

export type TreatmentDenormalized = Omit<TreatmentFromApi, "programIds"> & {
  programs: ProgramFromApi[];
};

export type ProgramsAndTreatments = {
  treatments: Record<string, TreatmentDenormalized>;
  programs: Record<
    string,
    Omit<ProgramFromApi, "treatmentId"> & { treatment: TreatmentFromApi }
  >;
  searchTree: Fuse<TreatmentDenormalized>;
};
